import React from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserHistory } from 'history';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as CustomThemeProvider } from './context/ThemeContext';
import { useTheme } from './context/ThemeContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'react-redux';
import { store } from './stores/store';
import './i18n';
import { Toaster } from 'react-hot-toast';

import 'assets/scss/material-kit-react.scss?v=1.10.0';

// pages for this product
import NodePage from './views/NodePage/NodePage.js';
import LoginPage from 'views/LoginPage/LoginPage.tsx';
import RegisterPage from 'views/RegisterPage/RegisterPage.tsx';
import DiscordCallback from 'views/AuthCallback/DiscordCallback.tsx';
import SupplierItemPage from './views/ItemPage/SupplierItemPage';
import UsersNodePage from './views/UsersNodePage/UsersNodePage';
import MehlbotSalesHistoryPage from './views/MehlbotSalesHistoryPage/MehlbotSalesHistoryPage';
import PresetPage from './views/MehlbotPickitPage/PresetPage';
import Season10ItemPage from './views/NewItemPage/Season10ItemPage';
import StaffPage from './views/StaffPage/StaffNodePage';
import UserOverviewPage from './views/StaffPage/StaffUserPage';
import StaffDiscordsPage from './views/StaffPage/Discords/StaffDiscordsPage';
import StaffOrderPage from './views/StaffOrderPage/StaffOrderPage';
import AnalyticsPage from './views/AnalyticsPage/AnalyticsPage';
import RareItemPage from 'views/ItemPage/RareItemPage';
import Layout from './views/Layout/Layout';
import NotFoundPage from './views/NotFoundPage/NotFoundPage';
import ProfilePage from './views/ProfilePage/ProfilePage';
import SettingsPage from 'views/SettingsPage/SettingsPage';
import OffersPage from './views/OffersPage/OffersPageV1';
import TradeListsPage from './views/TradeListsPage/TradeListsPage';
import Season9ItemPage from 'views/NewItemPage/Season9ItemPage';
import { ItemDetailPage } from './views/ItemDetailPage/ItemDetailPage';
import { PickitDetailPage } from './views/ProductDetailPage/ProductDetailPage';
import { GuildDetailPage } from './views/GuildDetailPage/GuildDetailPage';
import { UserDetailPage } from './views/UserDetailPage/UserDetailPage';
import { RulesetDetailPage } from './views/RulesetDetailPage/RulesetDetailPage';
import { StreamerDetailPage } from './views/StreamerDetailPage/StreamerDetailPage';
import { TournamentDetailPage } from './views/TournamentDetailPage/TournamentDetailPage';
import { HeaderLinksProvider } from 'components/Header/HeaderLinksContext';
// Tours pages
import CombinedTourPage from './views/TourPage/CombinedTourPage';
import ItemTrackerPage from './views/ItemTrackerPage/ItemTrackerPage';
import SavedSearchesPage from './views/SavedSearchesPage/SavedSearchesPage';
import TourPagesPage from './views/TourPagesPage/TourPagesPage';
import RecipesPage from './views/RecipesPage/RecipesPage';
import GuidesPage from './views/GuidesPage/GuidesPage';
import BreakpointsPage from './views/BreakpointsPage/BreakpointsPage';
import UserOffersPage from './views/OffersPage/UserOffersPage';
import CommunityPage from 'views/CommunityPage/CommunityPage';
import LogoutPage from 'views/LogoutPage/LogoutPage';
import LandingPage from 'views/LandingPage/LandingPageV1';
import SupplierPage from 'views/SupplierPage/SupplierPage';
import { primaryColor, secondaryColor } from './assets/jss/material-kit-react';
import WorkWithUs from 'views/WorkWithUs/WorkWithUs';
import EventModPage from './views/EventModPage/EventModPage';
import ImageGeneratorPage from './views/ImageGeneratorPage/ImageGeneratorPage';
import CharBuilderPage from './views/CharBuilderPage/CharBuilderPage';
import PriceIndexPage from './views/PriceIndexPage/PriceIndexPage';
import DiscordCompanionPage from './views/DiscordCompanionPage/DiscordCompanionPage';
import CurrentPrivateSearch from 'views/NewItemPage/CurrentPrivateSearch';
import CartPage from './views/CartPage/CartPage';
import ErrorBoundaryTest from './views/ErrorBoundaryTest/ErrorBoundaryTest';
import { GuideDetailPage } from 'views/GuideDetailPage/GuideDetailPage';
import { RequireAuth } from 'components/Auth/RequireAuth';
import ClassDetailPage from './views/ClassDetailPage/ClassDetailPage.tsx';
import ClassesPage from './views/ClassesPage/ClassesPage.tsx';
import DynamicPickitPacks from './views/StaffPage/DynamicPickitPacks';
import D2RSShopPage from 'views/PublicSearch/D2RSShopPage';
import DynamicPickitPackDetailPage from './views/DynamicPickitPackDetailPage/DynamicPickitPackDetailPage';

const hist = createBrowserHistory();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const App = () => {
  const { isDarkMode } = useTheme();

  const theme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: primaryColor,
        light: '#c45cd4',
        dark: '#385170',
        contrastText: '#ffffff',
      },
      secondary: {
        main: secondaryColor,
        light: secondaryColor,
        dark: secondaryColor,
        contrastText: '#000000',
      },
      info: {
        main: isDarkMode ? '#ffffff' : primaryColor,
        light: '#ffffff',
        dark: '#ffffff',
        contrastText: '#ffffff',
      },
      outlined: {
        main: isDarkMode ? '#142d4c' : '#ffffff',
        secondary: isDarkMode ? '#424242' : '#eeeeee',
        contrastText: isDarkMode ? '#ffffff' : '#142d4c',
      },
      navPill: {
        main: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#4242421c',
      },
      warning: {
        main: secondaryColor,
        light: '#f4cf3b',
        dark: '#ebc025',
        contrastText: '#ffffff',
      },
      background: {
        default: isDarkMode ? '#2d2d2d' : '#ffffff',
        paper: isDarkMode ? '#2d2d2d' : '#ffffff',
        header: '#212121',
        hoverNav: isDarkMode ? 'rgba(56, 81, 112, 0.1)' : secondaryColor,
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#000000',
        secondary: isDarkMode ? '#b3b3b3' : '#666666',
        navigation: isDarkMode ? '#ffffff' : 'inherit',
      },
      hoverNavSpecial: {
        color: isDarkMode ? secondaryColor : '#ffffff',
      },
    },
    components: {
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            width: '42px',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            padding: '8.5px 14px !important',
          },
          root: {
            padding: '0 !important',
            borderRadius: '6px',
            '& fieldset': {
              borderColor: '#9b9a9a !important',
            },
            '&:hover fieldset': {
              borderColor: '#FFD700 !important',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FFD700 !important',
              borderWidth: '2px !important',
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            padding: '8.5px 14px !important',
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            paddingTop: '0px',
            marginTop: '10px !important',
            marginBottom: '10px !important',
            '& .MuiOutlinedInput-root': {
              padding: '0',
            },
            '& .MuiInputBase-input': {
              height: 'auto',
            },
            '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
              padding: '8.5px 14px',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: '16px !important',
            fontWeight: '400 !important',
            maxWidth: 'calc(100% - 48px) !important',
            top: '0 !important',
            color: '#AAAAAA !important',
            lineHeight: '1.2',
            transform: 'translate(16px, 10px) scale(1) !important',
            '&.MuiInputLabel-shrink': {
              transform: 'translate(16px, -6px) scale(0.75) !important',
            },
            '&.Mui-focused': {
              color: isDarkMode ? '#ffffff' : '#FFD700 !important',
              maxWidth: 'calc(100% + 48px) !important',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            // This is equivalent to the 'MuiTextField-root' class
            marginTop: '0px !important',
            marginBottom: '0px !important',
            // Add anything else you want globally here
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '8.5px 14px !important',
            textAlign: 'left',
          },
          root: {
            textAlign: 'left',
            marginTop: '0px !important',
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: '#6b6b6b #2b2b2b',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: '#2b2b2b',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: '#6b6b6b',
              minHeight: 24,
              border: '3px solid #2b2b2b',
            },
            '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#959595',
            },
            '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
              backgroundColor: '#2b2b2b',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: isDarkMode ? '#ffffff' : primaryColor,
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          input: {
            '&.MuiInputBase-root': {
              marginBottom: '6px',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: primaryColor,
              opacity: isDarkMode ? '1' : '0.5',
              border: '1px solid rgba(241, 198, 43, 0.8)',
            },
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            zIndex: '999999 !important',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            overflow: 'none',
            '@media (max-width: 900px)': {
              overflow: 'auto', // applies when screen is 768px or smaller
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginTop: '10px !important',
            marginBottom: '10px !important',
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <HeaderLinksProvider>
        <CssBaseline />
        <BrowserRouter history={hist}>
          <Routes>
            <Route path="/" element={<Layout theme={theme} />}>
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route
                path="/tradelists"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <TradeListsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/node"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <NodePage />
                  </RequireAuth>
                }
              />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/auth/discord/callback" element={<DiscordCallback />} />
              <Route
                path="/supplier"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <SupplierPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/sitems"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <SupplierItemPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/users"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <UsersNodePage />
                  </RequireAuth>
                }
              />
              <Route
                path="/sales"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <MehlbotSalesHistoryPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/preset"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <PresetPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/items"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <CurrentPrivateSearch />
                  </RequireAuth>
                }
              />
              <Route
                path="/s10"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <Season10ItemPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/s9"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <Season9ItemPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/staff"
                element={
                  <RequireAuth requiredPermission="is_staff">
                    <StaffPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/staff/users"
                element={
                  <RequireAuth requiredPermission="is_staff">
                    <UserOverviewPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/staff/discords"
                element={
                  <RequireAuth requiredPermission="is_staff">
                    <StaffDiscordsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/staff/dynamic-pickit"
                element={
                  <RequireAuth requiredPermission="is_staff">
                    <DynamicPickitPacks />
                  </RequireAuth>
                }
              />
              <Route
                path="/orders"
                element={
                  <RequireAuth requiredPermission="is_d2rs">
                    <StaffOrderPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/offers"
                element={
                  <RequireAuth requiredPermission="is_d2rs">
                    <OffersPage />
                  </RequireAuth>
                }
              />
              <Route path="/offers-page" element={<UserOffersPage />} />
              <Route
                path="/analytics"
                element={
                  <RequireAuth requiredPermission="is_mehlbot">
                    <AnalyticsPage />
                  </RequireAuth>
                }
              />
              <Route
                path="/prescreen"
                element={
                  <RequireAuth requiredPermission="can_prescreen">
                    <RareItemPage />
                  </RequireAuth>
                }
              />
              <Route path="/item/:id" element={<ItemDetailPage />} />
              <Route path="/product/:id" element={<PickitDetailPage />} />
              <Route path="/guild/:id" element={<GuildDetailPage />} />
              <Route path="/guide/:id" element={<GuideDetailPage />} />
              <Route path="/user/:id" element={<UserDetailPage />} />
              <Route path="/ruleset/:id" element={<RulesetDetailPage />} />
              <Route path="/streamer/:id" element={<StreamerDetailPage />} />
              <Route path="/tournament/:id" element={<TournamentDetailPage />} />
              <Route path="/tracker" element={<ItemTrackerPage />} />
              <Route path="/saved-searches" element={<SavedSearchesPage />} />
              <Route path="/tour-pages" element={<TourPagesPage />} />
              <Route path="/recipes" element={<RecipesPage />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/breakpoints" element={<BreakpointsPage />} />
              <Route path="/communities" element={<CommunityPage />} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route path="/shop" element={<D2RSShopPage />} />
              <Route path="/get-involved" element={<WorkWithUs />} />
              <Route path="/event-mod" element={<EventModPage />} />
              <Route path="/image-generator" element={<ImageGeneratorPage />} />
              <Route path="/char-builder" element={<CharBuilderPage />} />
              <Route path="/price-index" element={<PriceIndexPage />} />
              <Route path="/discord-companion" element={<DiscordCompanionPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/error-test" element={<ErrorBoundaryTest />} />
              <Route path="/tour" element={<CombinedTourPage />} />
              <Route path="/classes" element={<ClassesPage />} />
              <Route path="/class/:className" element={<ClassDetailPage />} />
              <Route path="/pack/:id" element={<DynamicPickitPackDetailPage />} />
              <Route index element={<LandingPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster
          position="bottom-center"
          reverseOrder={false}
          gutter={8}
          toastOptions={{
            style: {
              background: isDarkMode ? '#333' : '#fff',
              color: isDarkMode ? '#fff' : '#333',
            },
          }}
        />
      </HeaderLinksProvider>
    </ThemeProvider>
  );
};

// Get the root element
const container = document.getElementById('root');

if (!container) {
  throw new Error('Failed to find the root element');
}

// Create a root
const root = createRoot(container);

// Add error boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '50px', fontFamily: 'Arial, sans-serif' }}>
          <h1>Something went wrong</h1>
          <p>We're sorry, but something went wrong. Please try refreshing the page.</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Render your app with error boundary
root.render(
  <ErrorBoundary>
    <Provider store={store}>
      <CustomThemeProvider>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </CustomThemeProvider>
    </Provider>
  </ErrorBoundary>
);
